import React from 'react';

import { Typography, makeStyles, Button } from '@material-ui/core';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';

import EditReportIcon from '../../assets/images/edit_document_icon.svg';
import IconReport from '../../assets/images/ic-doc.svg';
import useBlocRefHook from '../../utils/useBlocRef.hook';

const useStyles = makeStyles((theme) => ({
  '@global .MuiButton-label': {
    display: 'flex',
    gap: '8px',
  },
  buttonContainer: {
    '&:hover': {
      background: '#EEE',
      color: theme.palette.primary.main,
      cursor: 'pointer',
      fontWeight: 'bold',
    },
    alignItems: 'center',
    background: '#F5F5F5',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    gap: '16px',
    maxWidth: '400px',
    outline: 'none',
    padding: '16px',
    [theme.breakpoints.up('lg')]: {
      cursor: 'auto',
    },
    width: '100%',
  },
  buttonText: {
    fontSize: '14px',
    lineHeight: '21px',
    textAlign: 'center',
  },
  buttonWrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
    justifyContent: 'space-between',
    width: '100%',
  },
  deleteButton: {
    alignItems: 'center',
    display: 'flex',
    gap: '16px',
    justifyContent: 'left',
    width: '40px',
  },
  deleteIcon: {
    '&:hover': {
      borderColor: 'red',
      color: 'red',
    },
    border: '2px solid #aaa',
    borderRadius: '8px',
    color: '#999',
    cursor: 'pointer',
    height: '26px',
    padding: '5px',
    transition: 'all .8s ease',

    width: '31px',
  },
  editReportButton: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontWeight: 'bold',
    height: '40px',
    letterSpacing: 'normal',
    width: '250px',
  },
  printIcon: {
    '&:hover': {
      fill: '#333',
    },
    fill: '#777',
  },
  reportButton: {
    '& .MuiButton-label': {
      display: 'flex',
      gap: '8px',
    },
    '&:hover': {
      backgroundColor: '#FAFAFA',
      border: '1px solid #000000',
      borderColor: theme.palette.primary.main,
    },
    backgroundColor: '#FFFFFF',
    border: 'none',
    color: '#333',
    fontWeight: 'bold',
    height: '40px',
    letterSpacing: 'normal',
    width: '250px',
  },
  reportButtonContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: '16px',
    marginTop: '80px',
  },
  reportIcon: {
    height: 'auto',
    margin: '0 5px',
    width: '30px',
  },
  reportIcon2: {
    height: 'auto',
  },
  reportIconContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: '20px',
  },
  textReportTitle: {
    color: '#172B4D',
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '24px',
    margin: '16px 0 9px',
    textAlign: 'left',
  },
  viewAiReportButton: {
    '&:hover': {
      backgroundColor: '#565ee2',
    },
    backgroundColor: '#787FF6',
    color: 'white',
    height: '40px',
    letterSpacing: 'normal',
    width: '200px',
  },
}));

function CaseReportButton({ caseReport, readMode = false, caseId, caseStatus }) {
  const classes = useStyles();
  const blocRef = useBlocRefHook('patientReportBloc');
  const caseDetailsBlocRef = useBlocRefHook('caseDetailsBloc');

  const handleViewTextReport = () => blocRef.current.viewCaseReport(caseReport.content);
  const handleEditTextReport = () => caseDetailsBlocRef.current.editCaseReport(caseReport, caseId);

  const handlePrintReport = () => {
    // Create a hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    // Write the report content to the iframe
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <html>
        <head>
          <title>Mediphany Report</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 40px 20px;
              max-width: 800px;
              margin: 0 auto;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          ${caseReport.content}
        </body>
      </html>
    `);
    doc.close();

    // Wait for content to load before printing
    iframe.onload = () => {
      try {
        // Focus and print
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        // Remove the iframe after printing (with a delay to ensure print dialog is processed)
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      } catch (error) {
        console.error('Error printing report:', error);
        document.body.removeChild(iframe);
      }
    };
  };

  const showFinishedReadMode = readMode && !!caseReport;

  if (showFinishedReadMode) {
    return (
      <div>
        <Typography className={classes.textReportTitle}>Mediphany&apos;s Report</Typography>

        <button type="button" className={classes.buttonContainer} onClick={handleViewTextReport}>
          <div className={classes.buttonWrapper}>
            <div className={classes.reportIconContainer}>
              <img className={classes.reportIcon} src={IconReport} alt="Mediphany Report" />
              <Typography className={classes.buttonText}>Written Report {caseStatus !== 'completed' ? '(Draft)' : ''}</Typography>
            </div>

            <div title="Print Report">
              <PrintOutlinedIcon className={classes.printIcon} onClick={handlePrintReport} />
            </div>
          </div>
        </button>
      </div>
    );
  }

  if (!readMode) {
    return (
      <div className={classes.reportButtonContainer}>
        <Button color="primary" className={!caseReport ? classes.reportButton : classes.editReportButton} variant="contained" size="medium" onClick={handleEditTextReport}>
          <img style={{ fill: 'white !important' }} className={classes.reportIcon2} src={!caseReport ? IconReport : EditReportIcon} alt="Mediphany Report" />
          <Typography>{!caseReport ? 'Start Written Report' : 'Edit Written Report'}</Typography>
        </Button>

        {caseReport && (
          <div className={classes.deleteButton} title="Discard Text Report">
            <DeleteOutlinedIcon className={classes.deleteIcon} onClick={() => caseDetailsBlocRef.current.discardReport(caseReport.id, caseId)} title="Discard Written Report" />
          </div>
        )}
      </div>
    );
  }

  return null;
}

export default CaseReportButton;
